/**
 * Centralized dashboard selection logic with priority rules:
 * 1. Executive Dashboard (highest priority)
 * 2. Scorecard in ScripLogic folder
 * 3. Scorecard in FinanceLogic folder
 * 4. RebateLogic Submission in RebateLogic folder
 * 5. Any Scorecard workbook
 * 6. Any workbook (lowest priority)
 */

/**
 * Find the best dashboard based on priority rules
 * @param {Object} navigationData - The navigation data structure
 * @param {string} userHomepage - Optional user homepage preference
 * @returns {Object|null} - The selected dashboard or null if none found
 */
export const findBestDashboard = (navigationData, userHomepage = null) => {
  if (!navigationData || !Array.isArray(navigationData)) {
    return null
  }

  // Find the main Xevant navigation
  const xevantNav = navigationData.find(nav => nav.name === 'Xevant')
  if (!xevantNav) {
    return null
  }

  // Priority 1: Executive Dashboard
  const executiveDashboard = findExecutiveDashboard(xevantNav)
  if (executiveDashboard) {
    return executiveDashboard
  }

  // Priority 2: Scorecard in ScripLogic folder
  const scripLogicScorecard = findScorecardInFolder(xevantNav, 'ScripLogic')
  if (scripLogicScorecard) {
    return scripLogicScorecard
  }

  // Priority 3: Scorecard in FinanceLogic folder
  const financeLogicScorecard = findScorecardInFolder(xevantNav, 'FinanceLogic')
  if (financeLogicScorecard) {
    return financeLogicScorecard
  }

  // Priority 4: RebateLogic Submission in RebateLogic folder
  const rebateLogicSubmission = findRebateLogicSubmission(xevantNav)
  if (rebateLogicSubmission) {
    return rebateLogicSubmission
  }

  // Priority 5: Any Scorecard workbook
  const anyScorecard = findAnyScorecard(xevantNav, userHomepage)
  if (anyScorecard) {
    return anyScorecard
  }

  // Priority 6: Any workbook (fallback)
  return findAnyWorkbook(xevantNav)
}

/**
 * Find Executive Dashboard
 */
const findExecutiveDashboard = (xevantNav) => {
  // Check if Executive Dashboard is a direct child
  const execDashboardChild = xevantNav.children?.find(child => child.name === 'Executive Dashboard')
  if (execDashboardChild) {
    const execWorkbook = execDashboardChild.workbooks?.find(wb => wb.name.includes('Executive Dashboard'))
    if (execWorkbook) {
      return {
        contentUrl: execWorkbook.contentUrl,
        defaultView: execWorkbook.defaultView,
        name: execWorkbook.name,
        type: 'executive-dashboard'
      }
    }
  }

  // Check if Executive Dashboard is a direct workbook
  const execWorkbook = xevantNav.workbooks?.find(wb => wb.name.includes('Executive Dashboard'))
  if (execWorkbook) {
    return {
      contentUrl: execWorkbook.contentUrl,
      defaultView: execWorkbook.defaultView,
      name: execWorkbook.name,
      type: 'executive-dashboard'
    }
  }

  return null
}

/**
 * Find Scorecard in a specific folder
 */
const findScorecardInFolder = (xevantNav, folderName) => {
  const folder = xevantNav.children?.find(child => child.name === folderName)
  if (!folder) {
    return null
  }

  // Look for Scorecard workbook in the folder
  const scorecardWorkbook = folder.workbooks?.find(wb =>
    wb.name.toLowerCase().includes('scorecard')
  )

  if (scorecardWorkbook) {
    return {
      contentUrl: scorecardWorkbook.contentUrl,
      defaultView: scorecardWorkbook.defaultView,
      name: scorecardWorkbook.name,
      type: `scorecard-${folderName.toLowerCase()}`
    }
  }

  return null
}

/**
 * Find RebateLogic Submission in RebateLogic folder
 */
const findRebateLogicSubmission = (xevantNav) => {
  const rebateLogicFolder = xevantNav.children?.find(child => child.name === 'RebateLogic')
  if (!rebateLogicFolder) {
    return null
  }

  // Look for RebateLogic Submission workbook
  const submissionWorkbook = rebateLogicFolder.workbooks?.find(wb =>
    wb.name.toLowerCase().includes('submission') ||
    wb.name.toLowerCase().includes('proposal')
  )

  if (submissionWorkbook) {
    return {
      contentUrl: submissionWorkbook.contentUrl,
      defaultView: submissionWorkbook.defaultView,
      name: submissionWorkbook.name,
      type: 'rebate-logic-submission'
    }
  }

  return null
}

/**
 * Find any Scorecard workbook (with user homepage preference)
 */
const findAnyScorecard = (xevantNav, userHomepage = null) => {
  // If user has homepage preference, try to find it first
  if (userHomepage) {
    const homepageWorkbook = findWorkbookByContentUrl(xevantNav, userHomepage)
    if (homepageWorkbook && homepageWorkbook.name.toLowerCase().includes('scorecard')) {
      return {
        contentUrl: homepageWorkbook.contentUrl,
        defaultView: homepageWorkbook.defaultView,
        name: homepageWorkbook.name,
        type: 'homepage-scorecard'
      }
    }
  }

  // Look for Home Scorecard first
  const homeScorecard = findWorkbookByName(xevantNav, 'Home Scorecard')
  if (homeScorecard) {
    return {
      contentUrl: homeScorecard.contentUrl,
      defaultView: homeScorecard.defaultView,
      name: homeScorecard.name,
      type: 'home-scorecard'
    }
  }

  // Look for any Scorecard workbook
  const anyScorecard = findWorkbookByNamePattern(xevantNav, 'scorecard')
  if (anyScorecard) {
    return {
      contentUrl: anyScorecard.contentUrl,
      defaultView: anyScorecard.defaultView,
      name: anyScorecard.name,
      type: 'any-scorecard'
    }
  }

  return null
}

/**
 * Find any workbook as fallback
 */
const findAnyWorkbook = (xevantNav) => {
  // First try direct workbooks
  if (xevantNav.workbooks && xevantNav.workbooks.length > 0) {
    const firstWorkbook = xevantNav.workbooks[0]
    return {
      contentUrl: firstWorkbook.contentUrl,
      defaultView: firstWorkbook.defaultView,
      name: firstWorkbook.name,
      type: 'fallback-workbook'
    }
  }

  // Then try workbooks in children
  if (xevantNav.children) {
    for (const child of xevantNav.children) {
      if (child.workbooks && child.workbooks.length > 0) {
        const firstWorkbook = child.workbooks[0]
        return {
          contentUrl: firstWorkbook.contentUrl,
          defaultView: firstWorkbook.defaultView,
          name: firstWorkbook.name,
          type: 'fallback-workbook'
        }
      }
    }
  }

  return null
}

/**
 * Helper function to find workbook by content URL
 */
const findWorkbookByContentUrl = (xevantNav, contentUrl) => {
  // Check direct workbooks
  const directWorkbook = xevantNav.workbooks?.find(wb => wb.contentUrl === contentUrl)
  if (directWorkbook) {
    return directWorkbook
  }

  // Check workbooks in children
  if (xevantNav.children) {
    for (const child of xevantNav.children) {
      const childWorkbook = child.workbooks?.find(wb => wb.contentUrl === contentUrl)
      if (childWorkbook) {
        return childWorkbook
      }
    }
  }

  return null
}

/**
 * Helper function to find workbook by exact name
 */
const findWorkbookByName = (xevantNav, name) => {
  // Check direct workbooks
  const directWorkbook = xevantNav.workbooks?.find(wb => wb.name === name)
  if (directWorkbook) {
    return directWorkbook
  }

  // Check workbooks in children
  if (xevantNav.children) {
    for (const child of xevantNav.children) {
      const childWorkbook = child.workbooks?.find(wb => wb.name === name)
      if (childWorkbook) {
        return childWorkbook
      }
    }
  }

  return null
}

/**
 * Helper function to find workbook by name pattern
 */
const findWorkbookByNamePattern = (xevantNav, pattern) => {
  const lowerPattern = pattern.toLowerCase()

  // Check direct workbooks
  const directWorkbook = xevantNav.workbooks?.find(wb =>
    wb.name.toLowerCase().includes(lowerPattern)
  )
  if (directWorkbook) {
    return directWorkbook
  }

  // Check workbooks in children
  if (xevantNav.children) {
    for (const child of xevantNav.children) {
      const childWorkbook = child.workbooks?.find(wb =>
        wb.name.toLowerCase().includes(lowerPattern)
      )
      if (childWorkbook) {
        return childWorkbook
      }
    }
  }

  return null
}

/**
 * Generate dashboard path from selected dashboard
 * @param {Object} dashboard - The selected dashboard object
 * @returns {string|null} - The dashboard path or null if invalid
 */
export const generateDashboardPath = (dashboard) => {
  if (!dashboard || !dashboard.contentUrl || !dashboard.defaultView?.viewUrlName) {
    return null // Return null instead of hardcoded fallback
  }

  return `/dashboard/${dashboard.contentUrl}/${dashboard.defaultView.viewUrlName}`
}

/**
 * Find any available dashboard as a last resort fallback
 * @param {Array} navigationData - The navigation data
 * @returns {string|null} - A dashboard path or null
 */
const findAnyAvailableDashboard = (navigationData) => {
  if (!navigationData || !Array.isArray(navigationData)) {
    return null
  }

  const xevantNav = navigationData.find(nav => nav.name === 'Xevant')
  if (!xevantNav) {
    return null
  }

  // Try to find any workbook with valid contentUrl and defaultView
  const findValidWorkbook = (workbooks) => {
    if (!workbooks || !Array.isArray(workbooks)) return null

    for (const workbook of workbooks) {
      if (workbook.contentUrl && workbook.defaultView?.viewUrlName) {
        return `/dashboard/${workbook.contentUrl}/${workbook.defaultView.viewUrlName}`
      }
    }
    return null
  }

  // Check direct workbooks first
  const directPath = findValidWorkbook(xevantNav.workbooks)
  if (directPath) return directPath

  // Check workbooks in children
  if (xevantNav.children) {
    for (const child of xevantNav.children) {
      const childPath = findValidWorkbook(child.workbooks)
      if (childPath) return childPath
    }
  }

  return null
}

/**
 * Main function to get the best dashboard path
 * @param {Object} user - User object with navigation data
 * @param {Object} clientModules - Client modules data
 * @returns {Promise<string>} - The best dashboard path
 */
export const getBestDashboardPath = async (user, clientModules = null) => {
  // System Admin always goes to settings
  if (user.systemRole?.name === 'System Admin') {
    return '/settings'
  }

  // Try to get dashboard from user's cached navigation first
  const activeUserClient = user.userClients?.find(uc => uc.clientId === user.clientId)
  if (activeUserClient?.navigation) {
    try {
      const navigationData = JSON.parse(activeUserClient.navigation)
      const bestDashboard = findBestDashboard(navigationData, user.homepage)
      if (bestDashboard) {
        const path = generateDashboardPath(bestDashboard)
        if (path) return path
      }

      // If priority-based selection failed, try to find any available dashboard
      const fallbackPath = findAnyAvailableDashboard(navigationData)
      if (fallbackPath) return fallbackPath
    } catch (error) {
      console.warn('Failed to parse user navigation:', error)
    }
  }

  // If no cached navigation or no dashboard found, we need to wait for Tableau response
  // This would be handled by the calling code that makes the GraphQL query
  return null // Signal that we need to wait for Tableau data
}

// Default export with all the functions
export default {
  findBestDashboard,
  generateDashboardPath,
  getBestDashboardPath
}
